import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import "./ChangeLocation.css";
import { fetchProducts } from "../Redux/Actions/productVActions";

const ChangeLocation = ({ onLocationChange }) => {
  const dispatch = useDispatch();
  const productsData = useSelector((state) => state.productVReducer?.products || []);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedSubProduct, setSelectedSubProduct] = useState("");
  const [currentLocation, setCurrentLocation] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Charger les produits
  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        await dispatch(fetchProducts());
      } catch (error) {
        console.error("Erreur chargement produits:", error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [dispatch]);

  // Charger les localisations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get("/api/Locations/getAllLocations");
        setLocations(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Erreur chargement localisations :", err);
        setLocations([]);
      }
    };
    fetchLocations();
  }, []);

  // Mettre à jour la localisation actuelle quand un produit/sous-produit est sélectionné
  useEffect(() => {
    const updateCurrentLocation = () => {
      if (!selectedProduct && !selectedSubProduct) {
        setCurrentLocation("");
        return;
      }

      if (selectedSubProduct) {
        // Pour un sous-produit
        const subProduct = getSelectedSubProduct();
        if (subProduct) {
          const locationId = subProduct.localisation?._id || subProduct.localisation;
          setCurrentLocation(locationId || "");
        }
      } else if (selectedProduct) {
        // Pour un produit
        const product = productsData.find(p => p._id === selectedProduct);
        if (product && product.localisation) {
          setCurrentLocation(product.localisation._id || product.localisation);
        } else {
          // Si le produit n'a pas de localisation, prendre celle du premier sous-produit
          const firstSubProduct = product?.subProductsV?.[0];
          if (firstSubProduct) {
            const locationId = firstSubProduct.localisation?._id || firstSubProduct.localisation;
            setCurrentLocation(locationId || "");
          } else {
            setCurrentLocation("");
          }
        }
      }
    };

    updateCurrentLocation();
  }, [selectedProduct, selectedSubProduct, productsData]);

  // Obtenir les sous-produits du produit sélectionné
  const getSubProducts = () => {
    if (!selectedProduct) return [];
    const product = productsData.find(p => p._id === selectedProduct);
    return product?.subProductsV || [];
  };

  // Obtenir les informations du sous-produit sélectionné
  const getSelectedSubProduct = () => {
    if (!selectedSubProduct) return null;
    for (const product of productsData) {
      const subProduct = product.subProductsV?.find(sp => sp._id === selectedSubProduct);
      if (subProduct) return subProduct;
    }
    return null;
  };

  const handleChangeLocation = async () => {
    if ((!selectedProduct && !selectedSubProduct) || !newLocation) {
      alert("Veuillez sélectionner un produit/sous-produit et une nouvelle localisation");
      return;
    }

    if (newLocation === currentLocation) {
      alert("La nouvelle localisation est identique à la localisation actuelle");
      return;
    }

    setUpdating(true);
    try {
      const targetId = selectedSubProduct || selectedProduct;
      const isSubProduct = !!selectedSubProduct;

      if (isSubProduct) {
        // CORRECTION : Utiliser le bon endpoint pour mettre à jour un sous-produit
        await axios.put(`/api/ProductV/updateSubProductV/${targetId}`, {
          localisation: newLocation
        });
      } else {
        // CORRECTION : Utiliser le bon endpoint pour mettre à jour un produit
        await axios.put(`/api/ProductV/updateProductV/${targetId}`, {
          localisation: newLocation
        });
      }

      alert("Localisation mise à jour avec succès !");
      
      if (onLocationChange) {
        onLocationChange({
          id: targetId,
          type: isSubProduct ? 'subProduct' : 'product',
          oldLocation: currentLocation,
          newLocation: newLocation
        });
      }

      // Recharger les produits pour avoir les données à jour
      await dispatch(fetchProducts());
      
      // Réinitialiser les sélections
      setSelectedSubProduct("");
      setSelectedProduct("");
      setNewLocation("");
      setCurrentLocation("");

    } catch (error) {
      console.error("❌ Erreur changement localisation:", error);
      
      // Message d'erreur plus détaillé
      if (error.response?.status === 404) {
        alert("Erreur : L'endpoint de mise à jour n'existe pas. Vérifiez vos routes backend.");
      } else {
        alert("Erreur lors du changement de localisation: " + (error.response?.data?.message || error.message));
      }
    } finally {
      setUpdating(false);
    }
  };

  // Vérifier si les endpoints existent
  const checkEndpoints = async () => {
    try {
      // Tester l'endpoint de mise à jour des sous-produits
      const testResponse = await axios.get("/api/ProductV/getProductV");
      console.log("✅ Endpoint ProductV accessible");
    } catch (error) {
      console.error("❌ Endpoint ProductV inaccessible:", error);
    }
  };

  // Obtenir le nom de la localisation à partir de son ID
  const getLocationName = (locationId) => {
    if (!locationId) return "Non définie";
    const location = locations.find(loc => loc._id === locationId);
    return location ? location.name : "Inconnue";
  };

  const selectedSubProductInfo = getSelectedSubProduct();

  if (loading) {
    return (
      <div className="change-location-container">
        <div className="loading">Chargement des produits...</div>
      </div>
    );
  }

  return (
    <div className="change-location-container">
      <h2>Changer la Localisation</h2>

      {/* Sélection du produit */}
      <div className="location-form-group">
        <label>Produit</label>
        <select
          value={selectedProduct}
          onChange={(e) => {
            setSelectedProduct(e.target.value);
            setSelectedSubProduct("");
          }}
        >
          <option value="">Choisir un produit</option>
          {productsData.map((product) => (
            <option key={product._id} value={product._id}>
              {product.name} ({product.category})
            </option>
          ))}
        </select>
      </div>

      {/* Sélection du sous-produit */}
      <div className="location-form-group">
        <label>Sous-produit</label>
        <select
          value={selectedSubProduct}
          onChange={(e) => setSelectedSubProduct(e.target.value)}
          disabled={!selectedProduct}
        >
          <option value="">Choisir un sous-produit (optionnel)</option>
          {getSubProducts().map((subProduct) => (
            <option key={subProduct._id} value={subProduct._id}>
              {subProduct.name}
            </option>
          ))}
        </select>
        {selectedProduct && getSubProducts().length === 0 && (
          <div className="info-message">
            Ce produit n'a pas de sous-produits
          </div>
        )}
      </div>

      {/* Informations du produit/sous-produit sélectionné */}
      {(selectedProduct || selectedSubProduct) && (
        <div className="product-info">
          <h4>Informations {selectedSubProduct ? "du Sous-produit" : "du Produit"}</h4>
          {selectedSubProductInfo && (
            <>
              <div className="info-item">
                <strong>Nom:</strong>
                <span>{selectedSubProductInfo.name}</span>
              </div>
              <div className="info-item">
                <strong>Prix:</strong>
                <span>{selectedSubProductInfo.price} DT</span>
              </div>
            </>
          )}
          <div className="info-item">
            <strong>Localisation Actuelle:</strong>
            <span className="current-location">
              {getLocationName(currentLocation)}
            </span>
          </div>
        </div>
      )}

      {/* Sélection de la nouvelle localisation */}
      <div className="location-form-group">
        <label>Nouvelle Localisation</label>
        <select
          value={newLocation}
          onChange={(e) => setNewLocation(e.target.value)}
          disabled={!selectedProduct && !selectedSubProduct}
        >
          <option value="">Choisir une nouvelle localisation</option>
          {locations.map((location) => (
            <option key={location._id} value={location._id}>
              {location.name}
            </option>
          ))}
        </select>
      </div>

      {/* Aperçu du changement */}
      {newLocation && currentLocation && (
        <div className="change-preview">
          <h4>Aperçu du Changement</h4>
          <div className="preview-item">
            <span className="from">De: {getLocationName(currentLocation)}</span>
            <span className="arrow">→</span>
            <span className="to">Vers: {getLocationName(newLocation)}</span>
          </div>
        </div>
      )}

      <button 
        onClick={handleChangeLocation}
        disabled={
          (!selectedProduct && !selectedSubProduct) || 
          !newLocation || 
          newLocation === currentLocation ||
          updating
        }
        className="change-location-button"
      >
        {updating ? "Changement en cours..." : "Changer la Localisation"}
      </button>

      {/* Bouton de débogage pour vérifier les endpoints */}
      <button 
        onClick={checkEndpoints}
        style={{marginTop: '10px', background: '#666', fontSize: '12px', padding: '8px'}}
        type="button"
      >
        Vérifier les endpoints
      </button>
    </div>
  );
};

export default ChangeLocation;